import React from 'react';
import { mergeProps, useFocusVisible, useHover } from 'react-aria';
import { useMenuList } from '../MenuListContext';
import * as S from './styles';
import { MenuListItemProps } from './types';

function ForwardedMenuListItem<As extends React.ElementType = 'li'>(
  props: MenuListItemProps<As>,
  ref: React.ForwardedRef<React.ElementRef<As>>,
) {
  const { as, children, icon, isDisabled, isFocused, ...rest } = props;

  const Component: React.ElementType = as ?? 'li';

  const { density } = useMenuList();

  const { hoverProps, isHovered } = useHover(props);

  const { isFocusVisible } = useFocusVisible();

  return (
    <S.MenuListItemRoot
      as={Component}
      // isFocusVisible indicates whether the user is interacting with the keyboard.
      // if that's the case, hover styles should be disabled so that two items don't
      // get highlighted - one because of hover and another because of the keyboard
      data-hovered={!isFocusVisible && isHovered}
      data-focused={isFocused}
      ref={ref}
      $density={density}
      {...mergeProps(hoverProps, rest)}
    >
      <S.ItemInnerContainer $density={density}>
        {icon && <S.IconContainer $density={density}>{icon}</S.IconContainer>}
        {children}
      </S.ItemInnerContainer>
    </S.MenuListItemRoot>
  );
}

export const MenuListItem = React.forwardRef(ForwardedMenuListItem) as <
  As extends React.ElementType = 'li',
>(
  props: MenuListItemProps<As> & { ref?: React.Ref<React.ElementRef<As>> },
) => React.ReactElement | null;

export default MenuListItem;
